import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { runTask } from 'ember-lifeline';
import Icon from './icon';
import XIcon from '~icons/lucide/x';
import ExtractForm from './extract-form';

export interface ExtractSheetSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
  };
}

export default class ExtractSheet extends Component<ExtractSheetSignature> {
  @tracked isClosing = false;
  @tracked isClosingFromDrag = false;
  @tracked dragOffset = 0;

  private touchStartY = 0;
  private savedDragOffset = 0;

  @action
  startClose(): void {
    if (this.isClosing || this.isClosingFromDrag) return;
    if (this.dragOffset > 0) {
      this.savedDragOffset = this.dragOffset;
      this.dragOffset = 0;
      this.isClosingFromDrag = true;
    } else {
      this.isClosing = true;
    }
    runTask(
      this,
      () => {
        this.isClosing = false;
        this.isClosingFromDrag = false;
        this.savedDragOffset = 0;
        this.args.onClose();
      },
      250
    );
  }

  @action
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 0) return;
    this.touchStartY = event.touches[0]!.clientY;
    this.dragOffset = 0;
  }

  @action
  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 0) return;
    const delta = event.touches[0]!.clientY - this.touchStartY;
    this.dragOffset = Math.max(0, delta);
  }

  @action
  onTouchEnd() {
    if (this.dragOffset > 80) {
      this.startClose();
    } else {
      this.dragOffset = 0;
    }
  }

  @action
  onBackdropKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      event.preventDefault();
      this.startClose();
    }
  }

  get panelStyle() {
    // Closing from drag: tell the @keyframes where to start via a CSS custom property
    if (this.isClosingFromDrag) {
      return htmlSafe(`--drawer-drag-start: ${this.savedDragOffset}px;`);
    }
    // Actively dragging: follow the finger
    if (this.dragOffset > 0) {
      return htmlSafe(`transform: translateY(${this.dragOffset}px);`);
    }
    return htmlSafe('');
  }

  get panelClass(): string {
    const base =
      'bg-base-100 relative z-10 max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl shadow-2xl';
    if (this.dragOffset > 0) return base;
    if (this.isClosingFromDrag) return `${base} animate-drawer-out-from-drag`;
    if (this.isClosing) return `${base} animate-drawer-out`;
    return `${base} animate-drawer-in`;
  }

  get backdropClass(): string {
    const base = 'absolute inset-0 bg-black/50';
    if (this.isClosing || this.isClosingFromDrag)
      return `${base} animate-backdrop-out`;
    return `${base} animate-backdrop-in`;
  }

  <template>
    {{#if @isOpen}}
      {{! Full-screen overlay — backdrop tap closes the sheet }}
      <div class="fixed inset-0 z-50 flex flex-col justify-end">
        {{! Backdrop }}
        <div
          class={{this.backdropClass}}
          role="button"
          tabindex="0"
          aria-label="Close extract panel"
          {{on "click" this.startClose}}
          {{on "keydown" this.onBackdropKeyDown}}
        ></div>

        {{! Sheet panel }}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="extract-sheet-title"
          class={{this.panelClass}}
          style={{this.panelStyle}}
          {{on "touchstart" this.onTouchStart}}
          {{on "touchmove" this.onTouchMove}}
          {{on "touchend" this.onTouchEnd}}
        >
          {{! Drag handle — pull down to dismiss }}
          <div class="flex touch-none justify-center pt-3 pb-2">
            <div class="bg-base-300 h-1.5 w-12 cursor-grab rounded-full"></div>
          </div>

          {{! Form content }}
          <div class="px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div class="mb-4 flex items-center justify-between">
              <h2 id="extract-sheet-title" class="text-lg font-bold">Extract
                Content</h2>
              <button
                type="button"
                class="btn btn-ghost btn-sm btn-circle"
                aria-label="Close"
                {{on "click" this.startClose}}
              >
                <Icon @icon={{XIcon}} class="h-4 w-4" />
              </button>
            </div>
            <ExtractForm @onSuccess={{this.startClose}} />
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
